import React, { useState } from 'react';
import { 
  Newspaper, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Eye,
  Calendar
} from 'lucide-react';
import { Button, SearchSelect, DataTable } from '../../components/shared';
import { useAdminPressReleases } from '../../hooks/useAdminPressReleases';
import { useToastContext } from '../../contexts/ToastContext';
import CreatePressReleaseModal from '../../components/admin/CreatePressReleaseModal';
import { formatDate } from '../../utils/dateUtils';

const AdminPressReleases = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const { success, error: showError } = useToastContext();

  const {
    releases,
    isLoading,
    error,
    filters,
    handleFilterChange,
    statistics,
    createRelease,
    isCreating,
    updateRelease,
    isUpdating,
    toggleStatus,
    isToggling,
    deleteRelease,
    isDeleting
  } = useAdminPressReleases();

  const columns = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (release) => (
        <p className="text-sm font-medium text-gray-900 truncate">
          {release.title || 'N/A'}
        </p>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (release) => (
        <span className="text-sm text-gray-900 capitalize">
          {release.category || 'N/A'}
        </span>
      )
    },
    {
      key: 'release_date',
      label: 'Release Date',
      sortable: true,
      width: 'w-40',
      render: (release) => (
        <span className="text-sm text-gray-900 flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {release.release_date ? formatDate(release.release_date, 'short') : 'N/A'}
        </span>
      )
    },
    {
      key: 'views',
      label: 'Views',
      sortable: true,
      width: 'w-24',
      render: (release) => (
        <span className="text-sm text-gray-900 flex items-center gap-1">
          <Eye className="w-4 h-4" />
          {release.views || 0}
        </span>
      )
    },
    {
      key: 'is_published',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (release) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          release.is_published 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {release.is_published ? 'Published' : 'Draft'}
        </span>
      )
    }
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (release) => {
        setEditingRelease(release);
        setShowCreateModal(true);
      }
    },
    {
      label: release => release.is_published ? 'Unpublish' : 'Publish',
      icon: release => release.is_published ? XCircle : CheckCircle,
      variant: 'default',
      onClick: (release) => {
        toggleStatus(release.id, {
          onSuccess: () => {
            success('Status Updated', `Release ${release.is_published ? 'unpublished' : 'published'} successfully!`);
          },
          onError: (err) => showError('Status Update Failed', err.message || 'Failed to update status.')
        });
      },
      disabled: isToggling
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (release) => {
        if (window.confirm('Are you sure you want to delete this press release?')) {
          deleteRelease(release.id, {
            onSuccess: () => success('Release Deleted', 'Press release deleted successfully!'),
            onError: (err) => showError('Delete Failed', err.message || 'Failed to delete release.')
          });
        }
      },
      disabled: isDeleting
    }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Press Releases</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load press releases. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Newspaper className="w-6 h-6" />
                Press Releases
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage press releases and company announcements
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingRelease(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Release
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Releases</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{statistics.total_releases || 0}</p>
              </div>
              <Newspaper className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Published</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{statistics.published_releases || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-gray-600 mt-1">{statistics.draft_releases || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Views</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{statistics.total_views || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.is_published}
              onChange={(value) => handleFilterChange('is_published', value)}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'true', label: 'Published' },
                { value: 'false', label: 'Draft' }
              ]}
            />
            <div className="flex items-end">
              <input
                type="text"
                placeholder="Search releases..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => {
                  handleFilterChange('is_published', '');
                  handleFilterChange('search', '');
                }}
                variant="outline"
                className="w-full h-[42px]"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={releases}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search press releases..."
            onSearch={(searchTerm) => handleFilterChange('search', searchTerm)}
            emptyMessage="No press releases found"
            pageSize={10}
            showPagination={true}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreatePressReleaseModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingRelease(null);
          }}
          onSave={(data) => {
            if (editingRelease) {
              updateRelease(
                { id: editingRelease.id, data },
                {
                  onSuccess: () => {
                    success('Release Updated', 'Press release updated successfully!');
                    setShowCreateModal(false);
                    setEditingRelease(null);
                  },
                  onError: (err) => showError('Update Failed', err.message || 'Failed to update release.')
                }
              );
            } else {
              createRelease(data, {
                onSuccess: () => {
                  success('Release Created', 'Press release created successfully!');
                  setShowCreateModal(false);
                  setEditingRelease(null);
                },
                onError: (err) => showError('Creation Failed', err.message || 'Failed to create release.')
              });
            }
          }}
          release={editingRelease}
          isEdit={!!editingRelease}
          isLoading={isCreating || isUpdating}
        />
      )}
    </div>
  );
};

export default AdminPressReleases;

